import {
  ArrowLeftRight,
  Calendar,
  ChartSpline,
  FileIcon,
  LayoutPanelLeft,
  SettingsIcon,
  SquareActivity,
} from 'lucide-react';
import { lazyWithRetry } from './lazyWithRetry';

/**
 * ⚠️ Every page below MUST stay a dynamic import.
 *
 * This module is the whole app's route table, and `MainRouter` imports `privatePaths` from it
 * *statically* — so anything this file imports statically lands in the initial bundle, whatever
 * `lazy()` boundaries exist elsewhere. That is exactly what used to happen: one static import list
 * here put every landing page, the dashboard, the editor and data-analysis into `main.js`, and the
 * twelve `lazy()` calls in `MainRouter` split out ~7 kB between them.
 *
 * The `webpackChunkName` comments are load-bearing, not cosmetic: they merge these into three
 * chunks so a navigation costs one request, instead of ~25 separate ones. Keep the names in step
 * with the same comments in `MainRouter`, which point at several of these same modules — matching
 * names merge, a mismatch silently duplicates the module into two chunks.
 */
const ForgotPassword = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '@pages/auth/ForgotPassword'),
);
const Login = lazyWithRetry(() => import(/* webpackChunkName: "auth" */ '../pages/auth/Login'));
const LoginEmailSend = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '../pages/auth/LoginEmailSend'),
);
const LoginPassword = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '../pages/auth/LoginPassword'),
);
const LoginPhone = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '../pages/auth/LoginPhone'),
);
const LoginPhoneSms = lazyWithRetry(
  () => import(/* webpackChunkName: "auth" */ '../pages/auth/LoginPhoneSms'),
);

const Contact = lazyWithRetry(() => import(/* webpackChunkName: "landing" */ '../pages/Contact'));
const DataProtection = lazyWithRetry(
  () => import(/* webpackChunkName: "landing" */ '../pages/Contact/DataProtection'),
);
const Imprint = lazyWithRetry(
  () => import(/* webpackChunkName: "landing" */ '../pages/Contact/Imprint'),
);
const Faq = lazyWithRetry(() => import(/* webpackChunkName: "landing" */ '../pages/Faq'));
const Homepage = lazyWithRetry(() => import(/* webpackChunkName: "landing" */ '../pages/Homepage'));
const Partners = lazyWithRetry(() => import(/* webpackChunkName: "landing" */ '../pages/Partners'));
const Revision = lazyWithRetry(() => import(/* webpackChunkName: "landing" */ '../pages/Revision'));
const Team = lazyWithRetry(() => import(/* webpackChunkName: "landing" */ '../pages/Team'));
const ThankYou = lazyWithRetry(() => import(/* webpackChunkName: "landing" */ '../pages/ThankYou'));

const CompanyPage = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '@pages/Company'),
);
const PersonalRoleNewEdit = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '@pages/dashboard/AuditPermisionRole'),
);
const Audits = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '@pages/dashboard/Audits'),
);
const Overview = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '@pages/dashboard/Overview'),
);
const Requests = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '@pages/dashboard/Requests'),
);
const MyCompanySettings = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '@pages/MyCompanySettings'),
);
const PlatformAdmin = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '@pages/platform-admin'),
);
const ViewCompany = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '@pages/platform-admin/ViewCompany/ViewCompany'),
);
const DashboardLayout = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../components/DashboardLayout'),
);
const AccountSettings = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../pages/account/AccountSettings'),
);
const DataAnalysis = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../pages/dashboard/DataAnalysis'),
);
const Documents = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../pages/dashboard/Documents'),
);
const Editor = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../pages/dashboard/Editor'),
);
const Meetings = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../pages/dashboard/Meetings'),
);
const Settings = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../pages/dashboard/Settings'),
);
const StatusPage = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../pages/dashboard/Status/index'),
);
const Notifications = lazyWithRetry(
  () => import(/* webpackChunkName: "dashboard" */ '../pages/Notifications'),
);

export const Navigation = {
  mainNavigation: [
    {
      exact: true,
      path: '/',
      theme: 'green',
      name: '',
      main: () => <Homepage />,
    },
    {
      exact: true,
      path: '/login/phone',
      theme: 'green',
      name: '',
      main: () => <LoginPhone />,
    },
    {
      exact: true,
      path: '/login/phone/sms',
      theme: 'green',
      name: '',
      main: () => <LoginPhoneSms />,
    },
    {
      exact: true,
      path: '/audits',
      theme: 'green',
      name: '',
      main: () => <Audits />,
    },

    {
      exact: true,
      path: '/revision',
      name: 'revision',
      theme: 'gray',
      main: () => <Revision />,
    },
    {
      exact: true,
      path: '/team',
      name: 'team',
      theme: 'gray',
      main: () => <Team />,
    },
    {
      exact: true,
      path: '/partner',
      name: 'partner',
      theme: 'gray',
      main: () => <Partners />,
    },
    {
      exact: true,
      path: '/kontakt/:slug?',
      name: 'kontakt',
      theme: 'gray',
      main: () => <Contact />,
    },
    {
      exact: true,
      name: 'faq',
      path: '/faq',
      theme: 'gray',
      main: () => <Faq />,
    },
    {
      exact: true,
      name: 'imprint',
      path: '/imprint',
      theme: 'gray',
      main: () => <Imprint />,
    },
    {
      exact: true,
      name: 'data_protection',
      path: '/data_protection',
      theme: 'gray',
      main: () => <DataProtection />,
    },
    {
      exact: true,
      path: '/dashboard/:slug?',
      main: () => <DashboardLayout />,
    },
    {
      exact: true,
      path: '/login',
      main: () => <Login />,
    },
    {
      exact: true,
      name: 'thank-you',
      theme: 'gray',
      path: '/thank-you',
      main: () => <ThankYou />,
    },
    {
      exact: true,
      path: '/login/new-password',
      main: () => <LoginPassword />,
    },
    {
      exact: true,
      path: '/login/email',
      main: () => <ForgotPassword />,
    },
    {
      exact: true,
      path: '/login/email/send',
      main: () => <LoginEmailSend />,
    },

    // <Route exact path='/thank-you' component={ThankYou}/>
  ],

  adminPlatform: [
    {
      exact: true,
      path: '/audits',
      main: Audits,
    },
    {
      exact: true,
      path: '/company/settings',
      main: MyCompanySettings,
    },
    {
      exact: true,
      path: '/company/:id/:path',
      main: CompanyPage,
    },
    {
      exact: true,
      path: '/notifications',
      main: Notifications,
    },
    {
      exact: true,
      path: '/account/settings',
      main: AccountSettings,
    },
    // Every read behind `/superadmin` is `isSuperadmin`, and its 403 ends the session.
    {
      exact: true,
      path: '/admin/:path',
      main: PlatformAdmin,
      superadminOnly: true,
    },
    {
      exact: true,
      path: '/admin/companies/:type/:id/:path',
      main: ViewCompany,
      superadminOnly: true,
    },
  ],

  adminDashboardMain: [
    {
      exact: true,
      path: '/dashboard/:id/overview',
      name: 'dashboard',
      icon: <LayoutPanelLeft size={18} />,
      param: 'overview',
      main: () => <Overview />,
    },
    {
      exact: true,
      name: 'requests',
      path: '/dashboard/:id/requests',
      param: 'requests',
      icon: <ArrowLeftRight size={18} />,
      main: () => <Requests />,
    },
    {
      hide: true,
      exact: true,
      path: '/dashboard/:id/requests/:requestId',
      name: 'requests',
      param: 'requests',
      icon: <ArrowLeftRight size={18} />,
      main: () => <Requests />,
    },
    {
      exact: true,
      path: '/dashboard/:id/status',
      name: 'status',
      icon: <SquareActivity size={18} />,
      param: 'status',
      main: () => <StatusPage />,
    },
    {
      exact: true,
      path: '/dashboard/:id/documents',
      name: 'documents',
      icon: <FileIcon size={18} />,
      param: 'documents',
      main: () => <Documents />,
    },
    {
      exact: true,
      path: '/dashboard/:id/data-analysis',
      name: 'data_analysis',
      icon: <ChartSpline size={18} />,
      param: 'data-analysis',
      main: () => <DataAnalysis />,
    },
    {
      exact: true,
      path: '/dashboard/:id/meetings',
      name: 'meetings',
      icon: <Calendar size={18} />,
      param: 'meetings',
      main: () => <Meetings />,
    },
    {
      exact: true,
      hide: true,
      path: '/dashboard/:id/meetings/:meetingId',
      name: 'meetings',
      param: 'meetings',
      main: () => <Meetings />,
    },
    {
      exact: true,
      path: '/dashboard/:id/settings',
      name: 'settings',
      icon: <SettingsIcon size={18} />,
      param: 'settings',
      main: () => <Settings />,
    },
    {
      exact: true,
      path: '/dashboard/:id/team-role/edit/:idRole',
      main: () => <PersonalRoleNewEdit />,
    },
    {
      exact: true,
      path: '/dashboard/:id/role/add',
      main: () => <PersonalRoleNewEdit />,
    },
    {
      exact: true,
      path: '/dashboard/:id/documents/:documentId',
      name: 'documentEditor',
      main: () => <Editor />,
    },
  ],
};

export const privatePaths = [
  ...Navigation.adminPlatform.map((route) => route.path),
  ...Navigation.adminDashboardMain.map((route) => route.path),
];
